// ============================================================================
// Luminance + contrast helpers
// ----------------------------------------------------------------------------
// WCAG-style relative luminance and contrast ratio computation. Used to pick
// the more readable text color out of a small candidate set (typically the
// theme's `textOnPrimary` and `textOnSecondary` tokens) for a given background
// surface.
//
// The hex parser accepts `#rgb`, `#rrggbb`, `rgb(...)` and `rgba(...)` strings
// — i.e. anything `getComputedStyle()` is likely to return for a CSS variable
// — and falls back to `null` for unsupported formats so callers can keep
// their existing default.
//
// Copied locally from the parent blue-haven theme so this fork's Header and
// Footer can reach these helpers without deep-importing from a parent
// package's `src/` (forbidden by the schema linter). Keep in sync with
// `@vibesites/theme-blue-haven/src/utils/luminance.ts` if the parent's
// version changes.
// ============================================================================

export interface RGB {
  r: number;
  g: number;
  b: number;
}

/** Parse a CSS color string (`#rgb`, `#rrggbb`, `rgb(...)`, `rgba(...)`). */
export function parseColor(input: string | null | undefined): RGB | null {
  if (!input) return null;
  const s = input.trim();

  // #rgb
  let m = s.match(/^#([0-9a-f]{3})$/i);
  if (m) {
    const h = m[1];
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }

  // #rrggbb
  m = s.match(/^#([0-9a-f]{6})$/i);
  if (m) {
    const h = m[1];
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }

  // rgb(r, g, b) / rgba(r, g, b, a) — also handles the modern space-separated
  // `rgb(r g b)` syntax that getComputedStyle emits in newer engines.
  m = s.match(/^rgba?\(\s*(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)/i);
  if (m) {
    return {
      r: Math.round(parseFloat(m[1])),
      g: Math.round(parseFloat(m[2])),
      b: Math.round(parseFloat(m[3])),
    };
  }

  return null;
}

/** WCAG relative luminance for an sRGB color in the range [0, 1]. */
export function relativeLuminance(input: string | RGB | null | undefined): number {
  const rgb = typeof input === 'string' || input == null ? parseColor(input as string) : input;
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** WCAG contrast ratio between two colors. Returns a number in [1, 21]. */
export function contrastRatio(
  a: string | RGB | null | undefined,
  b: string | RGB | null | undefined,
): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * From a set of candidate text colors, return the one that has the highest
 * WCAG contrast ratio against the given background. Ties go to the first
 * candidate. Unparseable candidates are skipped.
 */
export function pickHighestContrast<T extends string>(
  background: string | null | undefined,
  candidates: readonly T[],
): T | null {
  let best: T | null = null;
  let bestRatio = -Infinity;
  for (const candidate of candidates) {
    if (!parseColor(candidate)) continue;
    const ratio = contrastRatio(background, candidate);
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = candidate;
    }
  }
  return best;
}

/** Light / dark text values used by `getContrastTextColor`. Match the values
    `getContrastTextColor` in `@vibesites/tokens` returns for the auto-computed
    `--vibe-color-text-on-primary` CSS variable, so consumers of this helper
    stay in lockstep with the runtime's own contrast picker. */
export const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
export const TEXT_ON_PRIMARY_DARK = '#1a1a1a';

/**
 * Returns the contrasting text color (`'#ffffff'` or `'#1a1a1a'`) for a given
 * background color. Mirrors `getContrastTextColor` from `@vibesites/tokens`
 * (currently module-private), including the `0.55` luminance threshold that
 * intentionally favors white text on medium tones — gold, tan, dusty blue,
 * etc. — where dark text is technically higher-contrast per WCAG but reads
 * as heavy / muddy.
 *
 * Prefer this over `pickHighestContrast` when choosing text against the
 * property's `primary` color: `pickHighestContrast` does pure WCAG math and
 * picks dark on those medium tones, which is the wrong visual answer (and
 * disagrees with what the runtime sets `--vibe-color-text-on-primary` to).
 *
 * Falls back to the white-on-dark side when the input can't be parsed, which
 * matches how the tokens generator treats unknown / missing colors.
 */
export function getContrastTextColor(bg: string | null | undefined): string {
  if (!bg) return TEXT_ON_PRIMARY_LIGHT;
  const lum = relativeLuminance(bg);
  return lum > 0.55 ? TEXT_ON_PRIMARY_DARK : TEXT_ON_PRIMARY_LIGHT;
}
